import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import PromoBanner from "@/components/PromoBanner";
import LandingDemo from "@/components/LandingDemo";
import AccentPicker from "@/components/AccentPicker";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Headphones, Scale, BookOpen, ArrowRight, Zap, Award, TrendingUp,
  Sun, Moon, Sparkles, Target, Trophy, CheckCircle2, Check, Quote,
  Gavel, ShieldCheck, Play, Crown, Keyboard, FileLock,
} from "lucide-react";

const MODULES = [
  {
    to: "/dictation", icon: Headphones, tag: "01 / Dictation",
    title: "Shorthand Audio Dictation",
    desc: "Real TTS at 60 / 80 / 100 / 120 WPM. Listen, transcribe, get word-by-word accuracy in seconds.",
  },
  {
    to: "/legal", icon: Scale, tag: "02 / Legal",
    title: "High Court Judgement Typing",
    desc: "Actual 500-1000 word judgements. Timed WPM race with live accuracy meter and certificate.",
  },
  {
    to: "/pitman", icon: BookOpen, tag: "03 / Pitman",
    title: "Pitman Steno Book Exercises",
    desc: "Every chapter from the book — shorthand image on one side, your transcript on the other.",
  },
  {
    to: "/typing", icon: Zap, tag: "04 / Speed",
    title: "Pure Typing Speed Drills",
    desc: "English + Hindi passages with live highlighter. Build muscle memory the professional way.",
  },
];

const STATS = [
  { k: "500+", v: "Practice items" },
  { k: "4", v: "Modules covered" },
  { k: "16", v: "Achievements to earn" },
  { k: "3-day", v: "Free trial" },
];

const FEATURES = [
  { icon: TrendingUp, title: "Adaptive difficulty", desc: "System nudges every session to your 88-92% sweet-spot for fastest growth." },
  { icon: Award, title: "Bronze → Gold badges", desc: "Sixteen milestones — WPM, accuracy, streaks, mastery. Every session moves you." },
  { icon: Target, title: "Mistake Practice", desc: "Every sentence you typed wrong lives in a drill queue until you master it at 95%+." },
  { icon: Trophy, title: "Weekly Live Tests", desc: "Compete against every student on the same passage at the same second." },
  { icon: Sparkles, title: "Certificates", desc: "Hit 60 WPM at 95% accuracy — download a signed certificate to attach to applications." },
  { icon: CheckCircle2, title: "Streaks & reminders", desc: "Daily nudges keep your streak alive. Miss one day and you'll get an email." },
];

const COURTS = [
  "Supreme Court of India", "Allahabad High Court", "Delhi High Court",
  "Rajasthan High Court", "M.P. High Court", "Bombay High Court",
  "Calcutta High Court", "Karnataka High Court", "Punjab & Haryana HC",
  "SSC Stenographer", "UP Police Computer Operator", "UP Police ASI",
  "ASI Confidential",
];

const TESTIMONIALS = [
  {
    name: "Anjali Verma", role: "Selected · Allahabad HC 2025",
    quote: "Real judgement drills ne mera confidence 40 WPM se 92 WPM tak le gaya. Formatting evaluation ne exact court style sikhaya.",
    stats: "40 → 92 WPM · 96% accuracy",
  },
  {
    name: "Rahul Sharma", role: "PS · Rajasthan HC",
    quote: "Pitman book ki har exercise, alag speeds ki dictation, aur mistake queue — sab kuch ek hi jagah. Best steno prep app.",
    stats: "18 mock certificates earned",
  },
  {
    name: "Sneha Iyer", role: "Supreme Court aspirant",
    quote: "Live tests aur weekly races ne discipline banai. Streak calendar dekh kar practice miss karne ka mann nahi karta.",
    stats: "142-day streak · Gold tier",
  },
];

const PLANS = [
  {
    name: "Free Trial", price: "₹0", period: "3 days",
    tag: "Try it out", cta: "Start free",
    features: [
      "All 4 modules unlocked (limited)",
      "Dictation @ 60-80 WPM",
      "10 legal judgements",
      "Basic accuracy report",
    ],
    highlight: false,
  },
  {
    name: "Judiciary Pass",
    price: "₹399", period: "month",
    quarterly: "₹999 / quarter",
    tag: "Most popular",
    cta: "Get Judiciary Pass",
    features: [
      "Everything unlocked — all modules",
      "Legal Formatting Typing (all HCs)",
      "Pitman Shorthand (1-231 exercises)",
      "UP Police Hindi + English typing",
      "SSC Stenographer full track",
      "Dictation @ 60-120 WPM",
      "Certificates + Live Tests + Ranks",
    ],
    highlight: true,
  },
  {
    name: "UP Police Pass",
    price: "₹149", period: "month",
    quarterly: "₹349 / quarter",
    tag: "State recruitment",
    cta: "Get UP Police Pass",
    features: [
      "Hindi Mangal typing (25 WPM)",
      "English typing (30 WPM)",
      "Both Hindi + English unlocked",
      "5-min exam simulation",
      "Live accuracy + punctuation scoring",
      "State-pattern mock tests",
    ],
    highlight: false,
  },
];

const FAQS = [
  { q: "Kaunse High Court exams ke liye ye platform ready hai?", a: "Supreme Court PA/PS aur India ke sabhi High Courts (Allahabad, Delhi, Rajasthan, Bombay, MP, Punjab & Haryana, Calcutta, Karnataka) ke stenographer exam syllabus ke hisaab se drills banaye gaye hain." },
  { q: "Accuracy kaise calculate hoti hai?", a: "Har word-by-word compare hota hai — insertion, deletion, substitution sab count hote hain. Formatting (bold, italic, alignment) alag se score karta hai taki student court-style format bhi seekhe." },
  { q: "Kya Hindi shorthand ke liye bhi content hai?", a: "Haan — Module 05 (आशु लिपिक) mein Hindi TTS dictation, aur Typing module mein Devanagari passages available hain." },
  { q: "Certificate download kaise milega?", a: "Kisi bhi module mein 60 WPM @ 95% accuracy touch karte hi automatic certificate generate ho jaata hai — direct download, signed with your name and score." },
  { q: "Free trial ke baad card charge hoga kya?", a: "Nahi. Trial ke liye card details bhi nahi maangi jaati. Aap manually plan choose karke pay karte ho." },
  { q: "Practice offline bhi ho sakti hai kya?", a: "Abhi platform browser-based hai. Har device pe login karke practice kar sakte ho — desktop, laptop, tablet — data cloud pe sync rehta hai." },
];

export default function Landing() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const primaryHref = user ? "/dashboard" : "/register";
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <PromoBanner />

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b border-brand-gold/15">
        <div className="max-w-7xl mx-auto h-16 px-4 sm:px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="w-9 h-9 bg-gradient-to-br from-[#F5D97F] to-[#D4AF37] rounded-lg flex items-center justify-center shadow-lg shadow-brand-gold/25">
              <Gavel className="w-4 h-4 text-[#0F172A]" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-base tracking-tight">StenoTypingBooster</div>
              <div className="hidden sm:block text-[9px] uppercase tracking-[0.3em] text-muted-foreground font-bold -mt-0.5">HIGH COURT PS prep</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={toggle} data-testid="theme-toggle" className="w-9 h-9 border border-brand-gold/25 rounded-lg flex items-center justify-center hover:bg-brand-gold/10 transition-colors" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="w-4 h-4 text-brand-gold" /> : <Moon className="w-4 h-4 text-brand-gold" />}
            </button>
            <AccentPicker testId="landing-accent-picker" />
            {user ? (
              <Link to="/dashboard">
                <Button className="rounded-lg bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-bold" data-testid="landing-dashboard-btn">
                  Dashboard <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-xs uppercase tracking-widest font-bold text-muted-foreground hover:text-brand-gold px-3 transition-colors" data-testid="landing-login-link">Login</Link>
                <Link to="/register">
                  <Button className="rounded-lg bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-bold shadow-lg shadow-brand-gold/20" data-testid="landing-signup-btn">
                    Start free
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 aurora-bg pointer-events-none" />
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-24 sm:pt-24 sm:pb-32 lg:grid lg:grid-cols-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/40 bg-brand-gold/5 rounded-full text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-8">
              <Sparkles className="w-3 h-3" /> Premium stenography examination platform
            </div>
            <h1 className="font-display text-[2rem] leading-[1.05] sm:text-6xl lg:text-7xl font-black tracking-tight sm:leading-[0.95] mb-6 text-balance break-words">
              <span className="gold-text">StenoTypingBooster</span>
            </h1>
            <p className="text-lg sm:text-2xl leading-relaxed max-w-xl mb-4 font-semibold">
              Master Shorthand. <span className="gold-text font-black">Type Faster.</span> Clear Your Exam.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl mb-10">
              Real dictation, real court judgements, real Pitman book exercises — with instant word-level accuracy scoring. Everything you need to walk into the exam hall ready.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to={primaryHref}>
                <Button size="lg" data-testid="landing-cta-primary" className="h-14 px-8 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-sm uppercase tracking-widest font-black shadow-xl shadow-brand-gold/25 hover:shadow-brand-gold/40 transition-all">
                  {user ? "Go to Dashboard" : "Start Free Practice"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <button
                onClick={() => setVideoOpen(true)}
                data-testid="landing-cta-demo"
                className="inline-flex items-center gap-3 h-14 px-6 rounded-xl border border-brand-gold/40 hover:border-brand-gold hover:bg-brand-gold/5 transition-all text-sm uppercase tracking-widest font-bold text-foreground"
              >
                <span className="w-8 h-8 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center">
                  <Play className="w-3.5 h-3.5 text-brand-gold ml-0.5" fill="currentColor" />
                </span>
                Watch Demo
              </button>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {STATS.map((s) => (
                <div key={s.v}>
                  <div className="font-mono text-3xl font-black tracking-tight gold-text">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual: floating cards */}
          <div className="hidden lg:block lg:col-span-5 relative h-[520px]">
            <div className="absolute top-4 right-0 w-72 glass-panel rounded-2xl p-6 animate-float shadow-2xl shadow-black/40">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-brand-emerald/20 border border-brand-emerald/40 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-brand-emerald" />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Latest attempt</div>
              </div>
              <div className="font-mono text-4xl font-black">96.4<span className="text-lg text-muted-foreground">% acc</span></div>
              <div className="font-mono text-2xl font-bold gold-text mt-1">82 WPM</div>
              <div className="divider-gradient my-4" />
              <div className="text-xs font-mono text-muted-foreground">↗ +4 WPM vs last week</div>
            </div>

            <div className="absolute top-52 left-0 w-64 glass-panel rounded-2xl p-6 animate-float shadow-2xl shadow-black/40" style={{ animationDelay: "0.7s" }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-brand-gold/20 border border-brand-gold/40 rounded-lg flex items-center justify-center">
                  <Crown className="w-4 h-4 text-brand-gold" />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Badge unlocked</div>
              </div>
              <div className="font-display font-black text-lg">Century Speed</div>
              <div className="text-xs text-muted-foreground mt-1">100+ WPM · Gold tier</div>
              <div className="mt-3 h-1.5 rounded-full bg-brand-gold/15 overflow-hidden">
                <div className="h-full w-[82%] bg-gradient-to-r from-[#E5C158] to-[#D4AF37] rounded-full" />
              </div>
            </div>

            <div className="absolute bottom-2 right-6 w-64 glass-panel rounded-2xl p-6 animate-float shadow-2xl shadow-black/40" style={{ animationDelay: "1.4s" }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-brand-blue/20 border border-brand-blue/40 rounded-lg flex items-center justify-center">
                  <Target className="w-4 h-4 text-brand-blue" />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Next up</div>
              </div>
              <div className="font-mono text-sm font-bold">Judgement · 750 words</div>
              <div className="text-xs text-muted-foreground mt-1 leading-relaxed">Suggested — you're crushing Level 3</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar — Courts marquee */}
      <section className="relative border-y border-brand-gold/15 bg-secondary/30 py-6 overflow-hidden" data-testid="trust-badges-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-4">
          <div className="text-center text-[10px] uppercase tracking-[0.4em] font-bold text-muted-foreground">
            Preparing aspirants for
          </div>
        </div>
        <div className="relative">
          <div className="flex marquee-track gap-12 whitespace-nowrap will-change-transform">
            {[...COURTS, ...COURTS].map((c, i) => (
              <div key={i} className="flex items-center gap-3 shrink-0">
                <ShieldCheck className="w-4 h-4 text-brand-gold" />
                <span className="font-display text-lg font-bold text-muted-foreground/80 tracking-tight">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated demo */}
      <LandingDemo />

      {/* Modules */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Four modules · One flow</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            Everything a HIGH COURT PS needs — in one place.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Every module was co-designed with working stenographers. Each session ends with a diff view showing exactly which words you missed and why.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <Link
                key={m.to}
                to={m.to}
                data-testid={`landing-module-${m.to.slice(1)}`}
                className="group relative metal-border rounded-2xl p-8 overflow-hidden bg-card/40 hover:-translate-y-1 transition-transform"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/8 via-transparent to-transparent opacity-70 pointer-events-none" />
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">{m.tag}</div>
                  <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center mb-6 group-hover:bg-brand-gold/20 group-hover:border-brand-gold transition-colors">
                    <Icon className="w-6 h-6 text-brand-gold" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-2xl font-black tracking-tight mb-3">{m.title}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{m.desc}</p>
                  <div className="text-xs uppercase tracking-[0.25em] font-bold text-brand-gold flex items-center gap-1.5 group-hover:gap-3 transition-all">
                    Open module <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-brand-gold/10">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Built to keep you practising</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight">
            Progress, celebrated at every session.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} data-testid="feature-item">
                <div className="w-11 h-11 rounded-xl bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-gold" />
                </div>
                <h3 className="font-display text-xl font-black tracking-tight mb-2">{f.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Government Exam Tracks */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-brand-gold/10" data-testid="exam-tracks-section">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Beyond judiciary · Government exams</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            Prep tracks for every recruitment stream.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Not just High Court PS — dedicated modules for SSC, UP Police and other government stenography &amp; typing recruitments.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              tag: "SSC Stenographer · Grade C / D",
              title: "SSC Stenographer Track",
              icon: Award,
              features: [
                "Skill-test @ 80 & 100 WPM (English & Hindi)",
                "10-min dictation → 50-min transcription",
                "Full syllabus mock tests",
                "Actual paper 1 & 2 pattern",
                "SSC-CGL commission answer key checker",
              ],
              cta: "Start SSC prep",
            },
            {
              tag: "UP Police · Computer Operator",
              title: "UP Police Computer Operator Typing",
              icon: Keyboard,
              features: [
                "Hindi Mangal typing (25 WPM target)",
                "English typing (30 WPM target)",
                "5-min real exam simulation",
                "Live accuracy meter · Punctuation scoring",
                "Full state pattern mock tests",
              ],
              cta: "Start UP Police prep",
            },
            {
              tag: "UP Police · Assistant Sub-Inspector",
              title: "UP Police ASI",
              icon: ShieldCheck,
              features: [
                "Stenography section drills",
                "Hindi & English typing sections",
                "Descriptive writing pattern",
                "Time-bound mock tests",
                "Category-wise ranking",
              ],
              cta: "Start ASI prep",
            },
            {
              tag: "ASI Confidential",
              title: "ASI Confidential Track",
              icon: FileLock,
              features: [
                "Confidential typing pattern",
                "Legal note-taking drills",
                "Speed target 30 WPM English / 25 Hindi",
                "Handwriting-friendly transcription",
                "Mock test with full pattern",
              ],
              cta: "Start Confidential prep",
            },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="relative metal-border rounded-2xl p-8 bg-card/40 hover:-translate-y-1 transition-transform overflow-hidden" data-testid={`exam-track-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-gold to-brand-emerald" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-brand-gold" strokeWidth={2} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">{t.tag}</div>
                </div>
                <h3 className="font-display text-2xl font-black tracking-tight mb-4">{t.title}</h3>
                <ul className="space-y-2 mb-6">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check className="w-4 h-4 shrink-0 mt-0.5 text-brand-emerald" strokeWidth={3} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={primaryHref}>
                  <button className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-black shadow-md">
                    {t.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comfort & Customization */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24 border-t border-brand-gold/10" data-testid="comfort-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Study for hours · Not eye strain</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-4">
              Dark mode by default. <span className="gold-text-soft">Your accent, your comfort.</span>
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-6">
              Every screen opens in a warm judicial dark palette so long practice sessions don't tire your eyes. Prefer light? One click flips it. Prefer a different vibe? Choose your accent — Champagne Gold, Emerald, Sapphire Blue, or Rose Copper — and every button, chart line, and heading follows instantly.
            </p>
            <ul className="grid grid-cols-2 gap-2 mb-6">
              {["Dark mode default", "One-click light toggle", "4 accent palettes", "Saved per device"].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-brand-emerald" strokeWidth={3} />
                  <span className="text-foreground/85">{f}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Try it now →</div>
              <div className="flex items-center gap-2">
                {[
                  { hex: "#E5C158", name: "Gold" },
                  { hex: "#10B981", name: "Emerald" },
                  { hex: "#3B82F6", name: "Sapphire" },
                  { hex: "#F472B6", name: "Rose" },
                ].map((c) => (
                  <div key={c.name} className="flex flex-col items-center gap-1">
                    <span className="w-9 h-9 rounded-full ring-2 ring-white/20 shadow-lg" style={{ background: c.hex }} />
                    <span className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Right side — palette preview mockup */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden metal-border bg-card/40 p-6 grain">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-brand-crimson" />
                <div className="w-2 h-2 rounded-full bg-brand-gold" />
                <div className="w-2 h-2 rounded-full bg-brand-emerald" />
                <div className="ml-auto text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Preview</div>
              </div>
              <div className="space-y-3">
                <div className="h-2 rounded-full bg-brand-gold/30 w-1/3" />
                <div className="h-2 rounded-full bg-foreground/20 w-full" />
                <div className="h-2 rounded-full bg-foreground/20 w-5/6" />
                <div className="h-2 rounded-full bg-foreground/20 w-2/3" />
                <div className="grid grid-cols-3 gap-2 pt-4">
                  <div className="h-16 rounded-xl bg-brand-gold/15 border border-brand-gold/40" />
                  <div className="h-16 rounded-xl bg-brand-emerald/15 border border-brand-emerald/40" />
                  <div className="h-16 rounded-xl bg-brand-purple/15 border border-brand-purple/40" />
                </div>
                <div className="pt-2 h-10 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] flex items-center justify-center text-[10px] uppercase tracking-widest font-black text-[#0F172A]">Start Practice</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Group Race — friends compete */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24 border-t border-brand-gold/10" data-testid="group-race-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="relative rounded-2xl overflow-hidden metal-border bg-card/40 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[10px] uppercase tracking-widest font-black text-brand-gold">Live · Group Race</div>
                <div className="text-[10px] font-mono text-muted-foreground">5:32 left</div>
              </div>
              <div className="space-y-3">
                {[
                  { name: "Anjali V.", wpm: 92, acc: 96, medal: "🥇", tint: "gold" },
                  { name: "Rahul S.", wpm: 87, acc: 94, medal: "🥈", tint: "gold" },
                  { name: "You", wpm: 84, acc: 95, medal: "🥉", tint: "emerald", self: true },
                  { name: "Sneha I.", wpm: 78, acc: 91, medal: "4", tint: "purple" },
                  { name: "Vikram K.", wpm: 71, acc: 88, medal: "5", tint: "crimson" },
                ].map((p) => (
                  <div key={p.name} className={`flex items-center gap-3 p-2 rounded-lg ${p.self ? "bg-brand-emerald/10 border border-brand-emerald/40" : "bg-secondary/40 border border-brand-gold/10"}`}>
                    <div className="w-8 h-8 rounded-full bg-background/60 flex items-center justify-center text-sm font-black">{p.medal}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{p.name}</div>
                      <div className="h-1 mt-1 rounded-full bg-brand-gold/15 overflow-hidden">
                        <div className={`h-full rounded-full ${p.tint === "gold" ? "bg-gradient-to-r from-[#E5C158] to-[#D4AF37]" : p.tint === "emerald" ? "bg-brand-emerald" : p.tint === "purple" ? "bg-brand-purple" : "bg-brand-crimson"}`} style={{ width: `${p.wpm}%` }} />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-black text-sm">{p.wpm} <span className="text-[9px] text-muted-foreground">WPM</span></div>
                      <div className="text-[10px] font-mono text-muted-foreground">{p.acc}%</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-crimson/40 bg-brand-crimson/10 rounded-full text-[11px] uppercase tracking-[0.3em] font-bold text-brand-crimson mb-4">
              <Zap className="w-3 h-3" /> Paid members only
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight mb-4">
              Race your friends. <span className="gold-text-soft">Beat them in real time.</span>
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed mb-6">
              Paid students can create a private group of up to <span className="font-black text-brand-gold">10 members</span>, invite each other by email, and race on the same passage at the same second. Live WPM bars, medal ranks, and a group leaderboard that keeps you sharp between mock tests.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
              {[
                "Create up to 10-member groups",
                "Invite by email · paid-only",
                "Live WPM & accuracy race",
                "Medal ranks + group leaderboard",
                "Custom passage or Live Test",
                "Post-race diff report per member",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-brand-emerald shrink-0" strokeWidth={3} />
                  <span className="text-foreground/85">{f}</span>
                </li>
              ))}
            </ul>
            <Link to={user ? "/groups" : primaryHref}>
              <Button className="h-12 px-6 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-black shadow-lg shadow-brand-gold/25" data-testid="group-race-cta">
                {user ? "Manage my groups" : "Unlock Group Races"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-brand-gold/10" data-testid="testimonials-section">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Real students · Real results</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight">
            The courts they now serve.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} data-testid="testimonial-card" className="relative metal-border rounded-2xl p-8 bg-card/40 hover:-translate-y-1 transition-transform overflow-hidden grain">
              <Quote className="absolute top-6 right-6 w-8 h-8 text-brand-gold/25" strokeWidth={1.5} />
              <p className="text-base leading-relaxed text-foreground/90 mb-6">"{t.quote}"</p>
              <div className="pt-6 border-t border-brand-gold/15">
                <div className="font-display font-black text-lg">{t.name}</div>
                <div className="text-xs uppercase tracking-widest font-bold text-brand-gold mt-1">{t.role}</div>
                <div className="text-xs font-mono text-muted-foreground mt-3">{t.stats}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-brand-gold/10" data-testid="pricing-section">
        <div className="mb-16 max-w-3xl text-center mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Transparent pricing</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            One plan away from your certificate.
          </h2>
          <p className="text-lg text-muted-foreground">No card required for trial. Cancel anytime. All prices in INR.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.name}
              data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`relative rounded-2xl p-8 flex flex-col ${
                p.highlight
                  ? "bg-gradient-to-b from-brand-gold/15 to-brand-gold/5 border-2 border-brand-gold shadow-2xl shadow-brand-gold/20 md:-translate-y-4"
                  : "bg-card/40 metal-border"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-[#E5C158] to-[#D4AF37] text-[#0F172A] text-[10px] uppercase tracking-widest font-black">
                  {p.tag}
                </div>
              )}
              {!p.highlight && (
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{p.tag}</div>
              )}
              <h3 className={`font-display text-2xl font-black tracking-tight mb-3 ${p.highlight ? "mt-2" : ""}`}>{p.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className={`font-display text-5xl font-black tracking-tight ${p.highlight ? "gold-text" : ""}`}>{p.price}</span>
                <span className="text-sm text-muted-foreground font-mono">/ {p.period}</span>
              </div>
              {p.quarterly && (
                <div className="text-xs font-mono text-muted-foreground mb-6" data-testid={`pricing-quarterly-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  or <span className="font-bold text-brand-gold">{p.quarterly}</span> · save ~17%
                </div>
              )}
              {!p.quarterly && <div className="mb-6" />}
              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check className={`w-4 h-4 shrink-0 mt-0.5 ${p.highlight ? "text-brand-gold" : "text-brand-emerald"}`} strokeWidth={3} />
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to={primaryHref}>
                <Button
                  className={`w-full h-12 rounded-xl text-xs uppercase tracking-widest font-black transition-all ${
                    p.highlight
                      ? "bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] shadow-lg shadow-brand-gold/25"
                      : "bg-transparent border-2 border-brand-gold/40 hover:border-brand-gold text-foreground hover:bg-brand-gold/10"
                  }`}
                  data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {p.cta} <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 border-t border-brand-gold/10" data-testid="faq-section">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Questions, answered</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight">
            Everything you might ask.
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              data-testid={`faq-item-${i}`}
              className="metal-border rounded-xl bg-card/40 px-5 border-b-0"
            >
              <AccordionTrigger className="text-left font-display font-bold text-lg py-5 hover:no-underline hover:text-brand-gold transition-colors">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed pb-5 text-base">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24">
        <div className="relative rounded-3xl border-2 border-brand-gold/40 bg-gradient-to-br from-brand-gold/15 via-brand-gold/5 to-transparent p-10 sm:p-16 text-center overflow-hidden">
          <div className="absolute inset-0 aurora-bg opacity-60 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/50 bg-brand-gold/10 rounded-full text-[11px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-8">
              <Crown className="w-3 h-3" /> Signed & downloadable
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6 text-balance">
              Your first typing certificate<br />is <span className="gold-text">3 days</span> away.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10">
              Start the free trial. Hit 60 WPM at 95% accuracy. Download your certificate. No card required.
            </p>
            <Link to={primaryHref}>
              <Button size="lg" data-testid="landing-cta-bottom" className="h-14 px-10 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-sm uppercase tracking-widest font-black shadow-2xl shadow-brand-gold/30">
                {user ? "Continue practising" : "Claim your 3-day trial"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-brand-gold/15 py-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-gradient-to-br from-[#F5D97F] to-[#D4AF37] rounded-md flex items-center justify-center">
              <Gavel className="w-3.5 h-3.5 text-[#0F172A]" strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-sm tracking-tight">StenoTypingBooster</div>
          </div>
          <div className="text-xs font-mono text-muted-foreground">© 2026 · Built for India's HIGH COURT PS aspirants</div>
        </div>
      </footer>

      {/* Video Demo Modal */}
      {videoOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setVideoOpen(false)}
          data-testid="video-demo-modal"
        >
          <div className="relative w-full max-w-3xl aspect-video rounded-2xl overflow-hidden metal-border bg-card" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="video-demo-close">✕</button>
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-8 aurora-bg">
              <div className="w-16 h-16 rounded-full bg-brand-gold/20 border border-brand-gold/40 flex items-center justify-center mb-4">
                <Play className="w-6 h-6 text-brand-gold ml-1" fill="currentColor" />
              </div>
              <div className="font-display text-2xl font-black tracking-tight mb-2">Demo video coming soon</div>
              <div className="text-sm text-muted-foreground max-w-md leading-relaxed">
                Scroll down and try the live diff engine below the hero — that's the real product working in real time.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
